import type {
  AuthBrowserSessionResult,
  AuthClientMetadata,
  AuthEnvironmentScope,
  AuthPairingCredentialResult,
  ServerAuthSessionMethod,
  AuthSessionId,
  AuthSessionState,
} from "@t3tools/contracts";
import { EnvironmentHttpCommonError } from "@t3tools/contracts";
import type { EnvironmentHttpCommonError as EnvironmentHttpCommonErrorType } from "@t3tools/contracts";
import { t } from "@lingui/core/macro";
import * as DateTime from "effect/DateTime";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { HttpClientError } from "effect/unstable/http";

import {
  getPairingTokenFromUrl,
  stripPairingTokenFromUrl as stripPairingTokenUrl,
} from "../../pairingUrl";

import { PrimaryEnvironmentHttpClient } from "./httpClient";
import { runPrimaryHttp } from "../../lib/runtime";
import * as Data from "effect/Data";
import * as Predicate from "effect/Predicate";

export class BootstrapHttpError extends Data.TaggedError("BootstrapHttpError")<{
  readonly message: string;
  readonly status: number;
  readonly authInvalidReason?:
    | "missing_credential"
    | "invalid_credential"
    | "credential_exhausted"
    | "rate_limited";
}> {}
const isBootstrapHttpError = (u: unknown): u is BootstrapHttpError =>
  Predicate.isTagged(u, "BootstrapHttpError");
const isEnvironmentHttpCommonError = Schema.is(EnvironmentHttpCommonError);

export interface ServerPairingLinkRecord {
  readonly id: string;
  readonly credential: string;
  readonly scopes: ReadonlyArray<AuthEnvironmentScope>;
  readonly subject: string;
  readonly label?: string;
  readonly createdAt: string;
  /** `null` means the link never expires. */
  readonly expiresAt: string | null;
  /** `null` = the link has never been exchanged for a session. */
  readonly lastUsedAt: string | null;
  readonly useCount: number;
  /** `null` means the link has no use limit. */
  readonly maxUses: number | null;
}

export interface ServerClientSessionRecord {
  readonly sessionId: AuthSessionId;
  readonly subject: string;
  readonly scopes: ReadonlyArray<AuthEnvironmentScope>;
  readonly method: ServerAuthSessionMethod;
  readonly client: AuthClientMetadata;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly lastConnectedAt: string | null;
  readonly connected: boolean;
  readonly current: boolean;
}

type ServerAuthGateState =
  | { status: "authenticated" }
  | {
      status: "requires-auth";
      auth: AuthSessionState["auth"];
      errorMessage?: string;
    };

let bootstrapPromise: Promise<ServerAuthGateState> | null = null;
let resolvedAuthenticatedGateState: ServerAuthGateState | null = null;
const AUTH_SESSION_ESTABLISH_TIMEOUT_MS = 2_000;
const AUTH_SESSION_ESTABLISH_STEP_MS = 100;

export function peekPairingTokenFromUrl(): string | null {
  return getPairingTokenFromUrl(new URL(window.location.href));
}

export function stripPairingTokenFromUrl() {
  const url = new URL(window.location.href);
  const next = stripPairingTokenUrl(url);
  if (next.toString() === url.toString()) {
    return;
  }
  window.history.replaceState({}, document.title, next.toString());
}

export function takePairingTokenFromUrl(): string | null {
  const token = peekPairingTokenFromUrl();
  if (!token) {
    return null;
  }
  stripPairingTokenFromUrl();
  return token;
}

/**
 * The last pairing credential this browser exchanged successfully. Reusable
 * (personal) links stay valid server-side, so when the session dies — expiry,
 * server update, credential-store reset — we can silently re-pair instead of
 * bouncing the user to the pairing screen. One-time credentials stored here
 * simply fail that silent attempt once and get cleared.
 */
const STORED_PAIRING_CREDENTIAL_KEY = "t3code:pairing-credential";

function readStoredPairingCredential(): string | null {
  try {
    const stored = window.localStorage.getItem(STORED_PAIRING_CREDENTIAL_KEY);
    return stored && stored.trim().length > 0 ? stored : null;
  } catch {
    return null;
  }
}

function storePairingCredential(credential: string) {
  try {
    window.localStorage.setItem(STORED_PAIRING_CREDENTIAL_KEY, credential);
  } catch {
    // Private browsing or blocked storage: silent re-pair is best-effort.
  }
}

function clearStoredPairingCredential() {
  try {
    window.localStorage.removeItem(STORED_PAIRING_CREDENTIAL_KEY);
  } catch {
    // Ignore: nothing to clear when storage is unavailable.
  }
}

export async function fetchSessionState(): Promise<AuthSessionState> {
  return retryTransientBootstrap(async () => {
    try {
      return await runPrimaryHttp(
        PrimaryEnvironmentHttpClient.pipe(
          Effect.flatMap((client) => client.auth.session({ headers: {} })),
        ),
      );
    } catch (error) {
      const status = readHttpApiStatus(error);
      const statusLabel = String(status ?? "unknown");
      throw new BootstrapHttpError({
        message: t`Failed to load server auth session state (${statusLabel}).`,
        status: status ?? 500,
      });
    }
  });
}

function readHttpApiStatus(error: unknown): number | null {
  if (isEnvironmentHttpCommonError(error)) {
    return readEnvironmentHttpErrorStatus(error);
  }
  return HttpClientError.isHttpClientError(error) && error.response !== undefined
    ? error.response.status
    : null;
}

function readEnvironmentHttpErrorStatus(error: EnvironmentHttpCommonErrorType): number {
  switch (error._tag) {
    case "EnvironmentRequestInvalidError":
      return 400;
    case "EnvironmentAuthInvalidError":
      return 401;
    case "EnvironmentScopeRequiredError":
    case "EnvironmentOperationForbiddenError":
      return 403;
    case "EnvironmentInternalError":
      return 500;
  }
}

function readHttpApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (!isEnvironmentHttpCommonError(error)) {
    return fallbackMessage;
  }
  switch (error._tag) {
    case "EnvironmentAuthInvalidError":
      return error.reason === "missing_credential"
        ? t`Authentication required.`
        : error.reason === "rate_limited"
          ? t`Too many pairing attempts. Wait a few minutes and try again.`
          : error.reason === "credential_exhausted"
            ? t`This pairing link has already been used on all of its devices. Request a new one.`
            : t`Invalid bootstrap credential.`;
    case "EnvironmentRequestInvalidError":
      return error.reason === "invalid_scope"
        ? t`Requested token scope is invalid.`
        : t`Requested scope exceeds the bootstrap credential grant.`;
    case "EnvironmentScopeRequiredError":
      return t`The authenticated token is missing required scope: ${error.requiredScope}.`;
    case "EnvironmentOperationForbiddenError":
      return t`This operation is not allowed for the current session.`;
    case "EnvironmentInternalError":
      return fallbackMessage;
  }
}

function isInvalidBootstrapCredentialError(error: unknown): boolean {
  return (
    isEnvironmentHttpCommonError(error) &&
    error._tag === "EnvironmentAuthInvalidError" &&
    error.reason !== "missing_credential" &&
    error.reason !== "credential_exhausted"
  );
}

async function exchangeBootstrapCredential(credential: string): Promise<AuthBrowserSessionResult> {
  return retryTransientBootstrap(async () => {
    try {
      return await runPrimaryHttp(
        PrimaryEnvironmentHttpClient.pipe(
          Effect.flatMap((client) => client.auth.browserSession({ payload: { credential } })),
        ),
      );
    } catch (error) {
      const status = readHttpApiStatus(error) ?? 500;
      const message = isInvalidBootstrapCredentialError(error)
        ? t`Invalid pairing token. Check the token and try again.`
        : readHttpApiErrorMessage(error, "").trim();
      throw new BootstrapHttpError({
        message: message || t`Failed to bootstrap auth session (${status}).`,
        status,
        ...(isEnvironmentHttpCommonError(error) && error._tag === "EnvironmentAuthInvalidError"
          ? { authInvalidReason: error.reason }
          : {}),
      });
    }
  });
}

async function waitForAuthenticatedSessionAfterBootstrap(): Promise<AuthSessionState> {
  const startedAt = Date.now();

  while (true) {
    const session = await fetchSessionState();
    if (session.authenticated) {
      return session;
    }

    if (Date.now() - startedAt >= AUTH_SESSION_ESTABLISH_TIMEOUT_MS) {
      throw new Error(t`Timed out waiting for authenticated session after bootstrap.`);
    }

    await waitForBootstrapRetry(AUTH_SESSION_ESTABLISH_STEP_MS);
  }
}

const TRANSIENT_BOOTSTRAP_STATUS_CODES = new Set([502, 503, 504]);
const BOOTSTRAP_RETRY_TIMEOUT_MS = 15_000;
const BOOTSTRAP_RETRY_STEP_MS = 500;

export async function retryTransientBootstrap<T>(operation: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  while (true) {
    try {
      return await operation();
    } catch (error) {
      if (!isTransientBootstrapError(error)) {
        throw error;
      }

      if (Date.now() - startedAt >= BOOTSTRAP_RETRY_TIMEOUT_MS) {
        throw error;
      }

      await waitForBootstrapRetry(BOOTSTRAP_RETRY_STEP_MS);
    }
  }
}

function waitForBootstrapRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function isTransientBootstrapError(error: unknown): boolean {
  if (isBootstrapHttpError(error)) {
    return TRANSIENT_BOOTSTRAP_STATUS_CODES.has(error.status);
  }

  if (error instanceof TypeError) {
    return true;
  }

  return error instanceof DOMException && error.name === "AbortError";
}

async function bootstrapServerAuth(): Promise<ServerAuthGateState> {
  const currentSession = await fetchSessionState();
  if (currentSession.authenticated) {
    return { status: "authenticated" };
  }

  const storedCredential = readStoredPairingCredential();
  if (storedCredential) {
    try {
      await exchangeBootstrapCredential(storedCredential);
      await waitForAuthenticatedSessionAfterBootstrap();
      return { status: "authenticated" };
    } catch (error) {
      // Revoked or consumed: stop retrying it. Rate-limited attempts keep the
      // credential — it may still be valid once the window passes. Transient
      // failures already exhausted their retry budget inside
      // exchangeBootstrapCredential, and the user still lands on the pairing
      // screen without an error banner.
      if (
        isBootstrapHttpError(error) &&
        error.status === 401 &&
        error.authInvalidReason !== "rate_limited"
      ) {
        clearStoredPairingCredential();
      }
    }
  }

  return {
    status: "requires-auth",
    auth: currentSession.auth,
  };
}

export async function submitServerAuthCredential(credential: string): Promise<void> {
  const trimmedCredential = credential.trim();
  if (!trimmedCredential) {
    throw new Error(t`Enter a pairing token to continue.`);
  }

  resolvedAuthenticatedGateState = null;
  await exchangeBootstrapCredential(trimmedCredential);
  storePairingCredential(trimmedCredential);
  bootstrapPromise = null;
  stripPairingTokenFromUrl();
}

export async function createServerPairingCredential(input?: {
  readonly label?: string;
  readonly scopes?: ReadonlyArray<AuthEnvironmentScope>;
  readonly ttlSeconds?: number;
  readonly maxUses?: number;
}): Promise<AuthPairingCredentialResult> {
  const trimmedLabel = input?.label?.trim();
  try {
    return await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) =>
          client.auth.pairingCredential({
            headers: {},
            payload: {
              ...(trimmedLabel ? { label: trimmedLabel } : {}),
              ...(input?.scopes ? { scopes: input.scopes } : {}),
              ...(input?.ttlSeconds !== undefined ? { ttlSeconds: input.ttlSeconds } : {}),
              ...(input?.maxUses !== undefined ? { maxUses: input.maxUses } : {}),
            },
          }),
        ),
      ),
    );
  } catch (error) {
    throw new Error(
      readHttpApiErrorMessage(
        error,
        t`Failed to create pairing credential (${readHttpApiStatus(error) ?? "unknown"}).`,
      ),
      { cause: error },
    );
  }
}

export async function listServerPairingLinks(): Promise<ReadonlyArray<ServerPairingLinkRecord>> {
  try {
    const pairingLinks = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.pairingLinks({ headers: {} })),
      ),
    );
    return pairingLinks.map((pairingLink) => {
      const timestamps = {
        createdAt: DateTime.formatIso(pairingLink.createdAt),
        expiresAt:
          pairingLink.expiresAt === null ? null : DateTime.formatIso(pairingLink.expiresAt),
        lastUsedAt:
          pairingLink.lastUsedAt === null ? null : DateTime.formatIso(pairingLink.lastUsedAt),
      };
      if (pairingLink.label === undefined) {
        return {
          id: pairingLink.id,
          credential: pairingLink.credential,
          scopes: pairingLink.scopes,
          subject: pairingLink.subject,
          createdAt: timestamps.createdAt,
          expiresAt: timestamps.expiresAt,
          lastUsedAt: timestamps.lastUsedAt,
          useCount: pairingLink.useCount,
          maxUses: pairingLink.maxUses,
        };
      }
      return {
        id: pairingLink.id,
        credential: pairingLink.credential,
        scopes: pairingLink.scopes,
        subject: pairingLink.subject,
        label: pairingLink.label,
        createdAt: timestamps.createdAt,
        expiresAt: timestamps.expiresAt,
        lastUsedAt: timestamps.lastUsedAt,
        useCount: pairingLink.useCount,
        maxUses: pairingLink.maxUses,
      };
    });
  } catch (error) {
    throw new Error(
      readHttpApiErrorMessage(
        error,
        t`Failed to load pairing links (${readHttpApiStatus(error) ?? "unknown"}).`,
      ),
      { cause: error },
    );
  }
}

export async function revokeServerPairingLink(id: string): Promise<void> {
  try {
    await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.revokePairingLink({ headers: {}, payload: { id } })),
      ),
    );
  } catch (error) {
    throw new Error(
      readHttpApiErrorMessage(
        error,
        t`Failed to revoke pairing link (${readHttpApiStatus(error) ?? "unknown"}).`,
      ),
      { cause: error },
    );
  }
}

export async function listServerClientSessions(): Promise<
  ReadonlyArray<ServerClientSessionRecord>
> {
  try {
    const clientSessions = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.clients({ headers: {} })),
      ),
    );
    return clientSessions.map((clientSession) => ({
      sessionId: clientSession.sessionId,
      subject: clientSession.subject,
      scopes: clientSession.scopes,
      method: clientSession.method,
      client: clientSession.client,
      issuedAt: DateTime.formatIso(clientSession.issuedAt),
      expiresAt: DateTime.formatIso(clientSession.expiresAt),
      lastConnectedAt:
        clientSession.lastConnectedAt === null
          ? null
          : DateTime.formatIso(clientSession.lastConnectedAt),
      connected: clientSession.connected,
      current: clientSession.current,
    }));
  } catch (error) {
    throw new Error(
      readHttpApiErrorMessage(
        error,
        t`Failed to load paired clients (${readHttpApiStatus(error) ?? "unknown"}).`,
      ),
      { cause: error },
    );
  }
}

export async function revokeServerClientSession(sessionId: AuthSessionId): Promise<void> {
  try {
    await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) =>
          client.auth.revokeClient({ headers: {}, payload: { sessionId } }),
        ),
      ),
    );
  } catch (error) {
    throw new Error(
      readHttpApiErrorMessage(
        error,
        t`Failed to revoke client session (${readHttpApiStatus(error) ?? "unknown"}).`,
      ),
      { cause: error },
    );
  }
}

export async function revokeOtherServerClientSessions(): Promise<number> {
  try {
    const result = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.revokeOtherClients({ headers: {} })),
      ),
    );
    return result.revokedCount;
  } catch (error) {
    throw new Error(
      readHttpApiErrorMessage(
        error,
        t`Failed to revoke other client sessions (${readHttpApiStatus(error) ?? "unknown"}).`,
      ),
      { cause: error },
    );
  }
}

export async function resolveInitialServerAuthGateState(): Promise<ServerAuthGateState> {
  if (resolvedAuthenticatedGateState?.status === "authenticated") {
    return resolvedAuthenticatedGateState;
  }

  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  const nextPromise = bootstrapServerAuth();
  bootstrapPromise = nextPromise;
  return nextPromise
    .then((result) => {
      if (result.status === "authenticated") {
        resolvedAuthenticatedGateState = result;
      }
      return result;
    })
    .finally(() => {
      if (bootstrapPromise === nextPromise) {
        bootstrapPromise = null;
      }
    });
}

export function __resetServerAuthBootstrapForTests() {
  bootstrapPromise = null;
  resolvedAuthenticatedGateState = null;
}
